// GitHub timeline → engine attention inputs and snapshot lists.
import type { AttentionInput, AttentionOverride, ThreadSummary, TimelineEvent } from '../engine/types';
import type { Actor, Reviewer, ReviewThread, TimelineItem } from '../api/queries';
import { compareTimes, parseTime, relTime } from './time';

/**
 * The version of the rules here that turn GitHub's answers into the
 * engine's input (`toTimelineEvents`, `threadSummaries`, `botsIn`,
 * `reviewerLogin`). Bump it with any change to what they give the engine:
 * attention this browser saved under another version is then not shown.
 */
export const ATTENTION_RULES_VERSION = 1;

export function reviewerLogin(r: Reviewer | null | undefined): string | null {
  if (!r) return null;
  if (r.__typename === 'Team') return null; // teams are not expanded
  return r.login;
}

function login(a: Actor | null | undefined): string {
  return a?.login ?? 'ghost';
}

export function isBot(a: Actor | null | undefined): boolean {
  return !!a && (a.__typename === 'Bot' || a.login.endsWith('[bot]'));
}

/** Milliseconds since the epoch, by the engine's parse; 0 when unparseable. */
function ms(at: string): number {
  return parseTime(at) ?? 0;
}

/**
 * When each PR commit reached GitHub, as well as the timeline can tell.
 * `committedDate` is the author's clock, not the push time (GitHub no longer
 * reports pushes for plain commits), so it is raised to at least:
 * - the previous commit's push time (commits arrive in PR order), and
 * - just after every review made on an earlier commit of the PR (a review
 *   on commit k was submitted before commit k+1 was pushed).
 */
export function pushTimes(items: TimelineItem[]): Map<string, string> {
  const order = new Map<string, number>();
  for (const it of items) if (it.__typename === 'PullRequestCommit' && !order.has(it.commit.oid)) order.set(it.commit.oid, order.size);
  // floor[i]: latest review time on a commit before index i.
  const reviewAt: number[] = new Array(order.size).fill(0);
  for (const it of items) {
    if (it.__typename !== 'PullRequestReview' || it.state === 'PENDING') continue;
    const k = it.reviewCommit ? order.get(it.reviewCommit.oid) : undefined;
    if (k === undefined) continue;
    reviewAt[k] = Math.max(reviewAt[k], ms(it.submittedAt ?? it.createdAt));
  }
  const out = new Map<string, string>();
  let floor = 0;
  let reviewedBefore = 0;
  let i = 0;
  for (const it of items) {
    if (it.__typename !== 'PullRequestCommit' || out.has(it.commit.oid)) continue;
    const committed = ms(it.commit.committedDate);
    const after = reviewedBefore ? reviewedBefore + 1 : 0;
    const at = Math.max(committed, floor, after);
    out.set(it.commit.oid, at === committed ? it.commit.committedDate : new Date(at).toISOString());
    floor = at;
    reviewedBefore = Math.max(reviewedBefore, reviewAt[i] ?? 0);
    i++;
  }
  return out;
}

/**
 * Map timeline items to attention events. Thread replies appear in the
 * timeline as COMMENTED reviews; a "review" by the PR author is a reply, so it
 * maps to `comment`. Commit pushes are timed by `pushTimes`.
 */
export function toTimelineEvents(
  items: TimelineItem[],
  pr: { author: string; createdAt: string },
): TimelineEvent[] {
  const out: TimelineEvent[] = [{ type: 'opened', at: pr.createdAt, actor: pr.author }];
  const pushed = pushTimes(items);
  for (const it of items) {
    switch (it.__typename) {
      case 'PullRequestCommit':
        out.push({
          type: 'push',
          at: pushed.get(it.commit.oid) ?? it.commit.committedDate,
          actor: it.commit.author?.user?.login ?? pr.author,
        });
        break;
      case 'HeadRefForcePushedEvent':
        // GitHub names the pusher only for force pushes; an unknown pusher
        // (deleted account) counts as the author, like a commit push.
        out.push({ type: 'push', at: it.createdAt, actor: it.actor?.login ?? pr.author, actorIsPusher: !!it.actor });
        break;
      case 'BaseRefForcePushedEvent':
        break;
      case 'ReviewRequestedEvent':
      case 'ReviewRequestRemovedEvent': {
        const reviewer = reviewerLogin(it.requestedReviewer);
        if (!reviewer) break;
        out.push({
          type: it.__typename === 'ReviewRequestedEvent' ? 'review_requested' : 'review_request_removed',
          at: it.createdAt,
          actor: login(it.actor),
          reviewer,
        });
        break;
      }
      case 'PullRequestReview': {
        if (it.state === 'PENDING') break;
        const at = it.submittedAt ?? it.createdAt;
        const actor = login(it.author);
        if (actor === pr.author) out.push({ type: 'comment', at, actor });
        else out.push({ type: 'review', at, actor, state: it.state });
        break;
      }
      case 'IssueComment':
        out.push({ type: 'comment', at: it.createdAt, actor: login(it.author) });
        break;
      case 'ReadyForReviewEvent':
        out.push({ type: 'ready_for_review', at: it.createdAt, actor: login(it.actor) });
        break;
      case 'ConvertToDraftEvent':
        out.push({ type: 'converted_to_draft', at: it.createdAt, actor: login(it.actor) });
        break;
      case 'MergedEvent':
        out.push({ type: 'merged', at: it.createdAt, actor: login(it.actor) });
        break;
      case 'ClosedEvent':
        out.push({ type: 'closed', at: it.createdAt, actor: login(it.actor) });
        break;
      case 'ReopenedEvent':
        out.push({ type: 'reopened', at: it.createdAt, actor: login(it.actor) });
        break;
    }
  }
  return out;
}

/** Bots seen anywhere in the timeline. */
export function botsIn(items: TimelineItem[], extra: (Actor | null | undefined)[] = []): string[] {
  const bots = new Set<string>();
  const add = (a: Actor | null | undefined) => {
    if (isBot(a)) bots.add(a!.login);
  };
  for (const a of extra) add(a);
  for (const it of items) {
    if ('actor' in it) add(it.actor);
    if ('author' in it) add(it.author);
  }
  return [...bots];
}

export interface ThreadLike {
  isResolved: boolean;
  comments: { author: { login: string } | null; pending?: boolean }[];
}

/** Published threads as the engine sees them (drafts are invisible to others). */
export function threadSummaries(threads: ThreadLike[]): ThreadSummary[] {
  const out: ThreadSummary[] = [];
  for (const t of threads) {
    const published = t.comments.filter((c) => !c.pending);
    if (!published.length) continue;
    out.push({
      resolved: t.isResolved,
      startedBy: published[0].author?.login ?? 'ghost',
    });
  }
  return out;
}

export function threadLike(t: ReviewThread): ThreadLike {
  return {
    isResolved: t.isResolved,
    comments: t.comments.nodes.map((c) => ({ author: c.author, pending: c.pullRequestReview?.state === 'PENDING' })),
  };
}

/**
 * The latest event's `at`, unchanged, chosen by instant as the engine
 * orders times (so it is a basis no event postdates); '' for none.
 */
export function latestEventAt(events: TimelineEvent[]): string {
  let latest = '';
  for (const e of events) if (compareTimes(e.at, latest) > 0) latest = e.at;
  return latest;
}

export function attentionInput(args: {
  author: string;
  draft: boolean;
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  requested: string[];
  events: TimelineEvent[];
  threads: ThreadSummary[];
  overrides: AttentionOverride[];
  bots: string[];
}): AttentionInput {
  return { ...args };
}

// ---------------------------------------------------------------- conversation rows

export type CommitItem = Extract<TimelineItem, { __typename: 'PullRequestCommit' }>;

/** Consecutive commits that fold into one run (more than three). */
export const RUN_MIN = 4;

/**
 * A row of the Overview's conversation: one timeline item, or a run of
 * RUN_MIN or more consecutive commits shown folded. A run's key is its
 * first commit, so it keeps its key (and whether it is unfolded) as later
 * commits join it.
 */
export type TimelineRow = { kind: 'item'; item: TimelineItem } | { kind: 'run'; key: string; commits: CommitItem[] };

/** True for an item the conversation shows no row for (a review not yet submitted). */
function rowless(it: TimelineItem): boolean {
  return it.__typename === 'PullRequestReview' && it.state === 'PENDING';
}

/**
 * The conversation's rows, in timeline order: items without a row are
 * left out (so they never break a run), and every run of RUN_MIN or more
 * consecutive commits becomes one run row.
 */
export function timelineRows(items: readonly TimelineItem[]): TimelineRow[] {
  const out: TimelineRow[] = [];
  let run: CommitItem[] = [];
  const flush = () => {
    if (run.length >= RUN_MIN) out.push({ kind: 'run', key: run[0].commit.oid, commits: run });
    else for (const item of run) out.push({ kind: 'item', item });
    run = [];
  };
  for (const it of items) {
    if (rowless(it)) continue;
    if (it.__typename === 'PullRequestCommit') {
      run.push(it);
      continue;
    }
    flush();
    out.push({ kind: 'item', item: it });
  }
  flush();
  return out;
}

/** Who wrote a commit: their GitHub login, else their git name. */
export function commitAuthor(c: CommitItem): string {
  return c.commit.author?.user?.login ?? c.commit.author?.name ?? 'someone';
}

/** A length of time as its largest whole unit: `40m`, `3h`, `2d`. */
function duration(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

/** What a folded run says of itself. */
export interface RunSummary {
  /** Its first and last commits. */
  first: string;
  last: string;
  /** "4 commits by bob and mara · over 3h, 2d ago". */
  text: string;
  /** What its combined diff shows, naming the merge commits whose other side it includes. */
  diffTitle: string;
}

/** "a", "a and b", "a, b and c". */
function list(items: readonly string[]): string {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : (items[0] ?? '');
}

/**
 * A run's summary line: how many commits, their authors in order of first
 * commit (three named, then "and N others"), and when: how long the
 * commits span (earliest to latest date, when two or more dates parse and
 * they are a minute or more apart), and how long ago the latest was.
 *
 * The combined diff compares the trees before the first commit and at the
 * last, so it holds whatever a merge commit in the run brought in from
 * another branch (the base, usually); its title says so.
 */
export function runSummary(commits: readonly CommitItem[], now = Date.now()): RunSummary {
  const authors = [...new Set(commits.map(commitAuthor))];
  const by = list(authors.length > 3 ? [...authors.slice(0, 2), `${authors.length - 2} others`] : authors);
  const dated = commits.flatMap((c) => {
    const t = parseTime(c.commit.committedDate);
    return t === null ? [] : [{ t, at: c.commit.committedDate }];
  });
  let when = '';
  if (dated.length) {
    const latest = dated.reduce((a, b) => (b.t > a.t ? b : a));
    const span = latest.t - Math.min(...dated.map((d) => d.t));
    const ago = relTime(latest.at, now);
    when = dated.length > 1 && span >= 60_000 ? `over ${duration(span)}, ${ago}` : ago;
  }
  const merges = commits.filter((c) => (c.commit.parents?.totalCount ?? 1) > 1).map((c) => c.commit.abbreviatedOid);
  const together = `Open the changes these ${commits.length} commits make together in Files`;
  return {
    first: commits[0].commit.oid,
    last: commits[commits.length - 1].commit.oid,
    text: `${commits.length} commits by ${by}${when ? ' · ' + when : ''}`,
    diffTitle: merges.length
      ? `${together}, including what merge commit${merges.length > 1 ? 's' : ''} ${list(merges)} brought in from other branches, such as the base branch`
      : together,
  };
}

// ---------------------------------------------------------------- snapshots

export interface Snapshot {
  /** Its place in the list, from 1: the snapshot's number wherever one is shown. */
  n: number;
  sha: string;
  short: string;
  at: string | null;
  /** When it became the head, as well as the timeline tells (`pushTimes`, or the force-push); null when unknown. */
  pushedAt: string | null;
  kind: 'commit' | 'force-push' | 'replaced';
  label: string;
  actor: string | null;
}

/**
 * Snapshots are head SHAs the PR pointed at: every commit, plus both sides of
 * head force-pushes (the "before" side may no longer appear as a commit).
 */
export function snapshotsFrom(items: TimelineItem[], headRefOid: string): Snapshot[] {
  const out: Snapshot[] = [];
  const seen = new Set<string>();
  const push = (s: Omit<Snapshot, 'n'>) => {
    if (seen.has(s.sha)) return;
    seen.add(s.sha);
    out.push({ n: out.length + 1, ...s });
  };
  const pushed = pushTimes(items);
  // A force-push's after side the timeline also lists as a commit is shown
  // at that commit, so commits stay in order.
  const commitIdx = new Map<string, number>();
  items.forEach((it, i) => {
    if (it.__typename === 'PullRequestCommit' && !commitIdx.has(it.commit.oid)) commitIdx.set(it.commit.oid, i);
  });
  const pushedAfter = new Map<string, Omit<Snapshot, 'n'>>();
  items.forEach((it, i) => {
    if (it.__typename === 'PullRequestCommit') {
      push(
        pushedAfter.get(it.commit.oid) ?? {
          sha: it.commit.oid,
          short: it.commit.abbreviatedOid,
          at: it.commit.committedDate,
          pushedAt: pushed.get(it.commit.oid) ?? null,
          kind: 'commit',
          label: it.commit.messageHeadline,
          actor: it.commit.author?.user?.login ?? it.commit.author?.name ?? null,
        },
      );
    } else if (it.__typename === 'HeadRefForcePushedEvent') {
      if (it.beforeCommit)
        push({
          sha: it.beforeCommit.oid,
          short: it.beforeCommit.abbreviatedOid,
          at: null,
          pushedAt: null,
          kind: 'replaced',
          label: 'Before force-push',
          actor: null,
        });
      if (it.afterCommit) {
        const snap: Omit<Snapshot, 'n'> = {
          sha: it.afterCommit.oid,
          short: it.afterCommit.abbreviatedOid,
          at: it.createdAt,
          pushedAt: it.createdAt,
          kind: 'force-push',
          label: 'Force-pushed by ' + (it.actor?.login ?? 'someone'),
          actor: it.actor?.login ?? null,
        };
        if ((commitIdx.get(it.afterCommit.oid) ?? -1) > i) pushedAfter.set(it.afterCommit.oid, snap);
        else push(snap);
      }
    }
  });
  if (headRefOid && !seen.has(headRefOid))
    push({ sha: headRefOid, short: headRefOid.slice(0, 7), at: null, pushedAt: null, kind: 'commit', label: 'Head', actor: null });
  return out;
}

/** The commit a comment was left on: snapshot `n` of the list, or (`n` null) a commit that is none of them. */
export interface LeftOn {
  sha: string;
  short: string;
  n: number | null;
}

/**
 * The snapshot a comment was left on. A comment with a commit (an inline
 * review comment's `originalCommit`, a review's commit, an anchored
 * comment's marker) was left on that commit. One without (a conversation
 * comment) was left on the head when it was written: the snapshot with the
 * latest push at or before `at` (the later one in the list on a tie), or
 * the first snapshot when no known push precedes it. Null without snapshots.
 * The "Head" snapshot `snapshotsFrom` appends when the timeline has not
 * caught up has no push time (`pushedAt` null), so a comment without a
 * commit is never dated to it; one naming its commit still is.
 */
export function commentSnapshot(snaps: Snapshot[], c: { commit?: string | null; at: string }): LeftOn | null {
  if (c.commit) {
    const s = snaps.find((x) => x.sha === c.commit);
    return s ? { sha: s.sha, short: s.short, n: s.n } : { sha: c.commit, short: c.commit.slice(0, 7), n: null };
  }
  if (!snaps.length) return null;
  const at = parseTime(c.at);
  let best: Snapshot | null = null;
  let bestAt = -Infinity;
  for (const s of snaps) {
    const t = s.pushedAt === null ? null : parseTime(s.pushedAt);
    if (t === null || at === null || t > at || t < bestAt) continue;
    best = s;
    bestAt = t;
  }
  const s = best ?? snaps[0];
  return { sha: s.sha, short: s.short, n: s.n };
}

/** What decides whether a comment is outdated: GitHub's flag where it has one, and whether the comment names a line. */
export interface OutdatedBasis {
  /** GitHub's `isOutdated` (review threads); it wins when given. */
  github?: boolean;
  /** The comment is anchored to a line (a review thread, or a comment with a changelist:anchor marker). */
  anchored?: boolean;
}

/**
 * Whether a comment left on `on` is outdated. GitHub's flag decides where
 * it is given. Otherwise only a comment anchored to a line can be, when it
 * was left on a snapshot other than the head: a comment on the pull
 * request as a whole never is.
 */
export function isOutdated(on: LeftOn, head: string, basis: OutdatedBasis): boolean {
  if (basis.github !== undefined) return basis.github;
  return !!basis.anchored && on.sha !== head;
}

/**
 * The commit to date comment `i` of a review thread by (see
 * `commentSnapshot`): the first comment's original commit; for a reply,
 * its own original commit only when that differs from the first's (GitHub
 * reports a reply with its thread's), else null, so the reply is dated by
 * when it was written.
 */
export function commentCommit(comments: readonly { originalCommit: { oid: string } | null }[], i: number): string | null {
  const own = comments[i]?.originalCommit?.oid ?? null;
  if (i === 0) return own;
  return own && own !== comments[0]?.originalCommit?.oid ? own : null;
}
